import React, { useId, useState, useMemo } from 'react';
import rawCatalog from '../../packages/typography/google-fonts-catalog.json';

export interface FontEntry {
  name: string;
  cat: string;
}

const CATEGORY_NAMES: Record<string, string> = {
  all: 'All styles',
  sans: 'Sans serif',
  serif: 'Serif',
  display: 'Display',
  handwriting: 'Handwriting',
  mono: 'Monospace'
};

const BUNDLED_FONTS: FontEntry[] = [
  { name: 'Cinzel Decorative', cat: 'display' },
  { name: 'Bebas Neue', cat: 'display' },
  { name: 'Poppins', cat: 'sans' },
  { name: 'Lato', cat: 'sans' },
  { name: 'Fira Sans', cat: 'sans' },
  { name: 'Ubuntu', cat: 'sans' },
  { name: 'IBM Plex Serif', cat: 'serif' },
  { name: 'IBM Plex Mono', cat: 'mono' },
  { name: 'Intel One Mono', cat: 'mono' },
  { name: 'Atkinson Hyperlegible', cat: 'sans' },
  { name: 'Fondamento', cat: 'handwriting' },
  { name: 'VT323', cat: 'mono' },
  { name: 'Basic', cat: 'sans' }
];

export const ALL_FONTS: FontEntry[] = (() => {
  const seen = new Set<string>();
  const list: FontEntry[] = [];
  for (const f of BUNDLED_FONTS) {
    seen.add(f.name.toLowerCase().replace(/[\s_-]/g, ''));
    list.push(f);
  }
  for (const item of (rawCatalog as FontEntry[])) {
    const key = item.name.toLowerCase().replace(/[\s_-]/g, '');
    if (!seen.has(key)) {
      seen.add(key);
      list.push(item);
    }
  }
  return list;
})();

const POPULAR_FONTS = [
  'Cinzel Decorative',
  'Bebas Neue',
  'Poppins',
  'Orbitron',
  'Fondamento',
  'Creepster',
  'MedievalSharp',
  'VT323',
  'IBM Plex Serif',
  'Playfair Display',
  'Permanent Marker'
];

function norm(s: string): string {
  return (s || '').toLowerCase().replace(/[\s_-]/g, '');
}

export function FontPicker({
  names,
  value,
  disabled,
  onChoose
}: {
  names?: readonly string[];
  value: string;
  disabled: boolean;
  onChoose: (name: string) => Promise<void>;
}) {
  const searchId = useId(), listId = useId(), catId = useId();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const catalog = useMemo(() => {
    if (names && names.length > 0 && names.length < 50) {
      // If a restricted subset was explicitly passed by tests
      return ALL_FONTS.filter(f => names.some(n => norm(n) === norm(f.name)));
    }
    return ALL_FONTS;
  }, [names]);

  const matching = useMemo(() => {
    const q = query.trim().toLowerCase();
    return catalog.filter(font => {
      const matchesCat = category === 'all' || font.cat === category;
      if (!matchesCat) return false;
      if (!q) return true;
      return font.name.toLowerCase().includes(q) || font.cat.toLowerCase().includes(q);
    });
  }, [catalog, query, category]);

  async function choose(name: string) {
    if (disabled || busy) return;
    setBusy(true);
    setError('');
    try {
      await onChoose(name);
      setOpen(false);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The font could not be loaded.');
    } finally {
      setBusy(false);
    }
  }

  const selectedDisplay = ALL_FONTS.find(f => norm(f.name) === norm(value))?.name || value || 'Basic';

  return (
    <div className="lab-font-picker-wrap" style={{ margin: '8px 0 14px' }}>
      <label style={{ fontSize: '12px', fontWeight: 700, display: 'block', marginBottom: 4 }}>
        Font Choice
      </label>

      {/* Popular quick-select pills */}
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
        {POPULAR_FONTS.map(f => {
          const isSelected = norm(value) === norm(f);
          return (
            <button
              type="button"
              key={f}
              disabled={disabled || busy}
              aria-pressed={isSelected}
              onClick={() => choose(f)}
              style={{
                fontSize: '11px',
                padding: '4px 8px',
                minHeight: '28px',
                borderRadius: '14px',
                border: isSelected ? '2px solid #b91f32' : '1px solid #d1d5db',
                background: isSelected ? '#fcebee' : '#f9fafb',
                color: isSelected ? '#b91f32' : '#374151',
                fontWeight: isSelected ? 700 : 500,
                cursor: 'pointer'
              }}
            >
              {f.split(' ')[0]}
            </button>
          );
        })}
      </div>

      {/* Accordion panel for full 1,580+ font catalog */}
      <details
        className="lab-font-picker"
        open={open}
        onToggle={event => setOpen(event.currentTarget.open)}
        style={{
          maxWidth: '100%',
          background: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          padding: '6px 10px'
        }}
      >
        <summary
          aria-disabled={disabled}
          onClick={event => {
            if (disabled) event.preventDefault();
          }}
          style={{ cursor: 'pointer', fontWeight: 600, fontSize: '12px' }}
        >
          Font: <strong>{selectedDisplay}</strong>{' '}
          <small style={{ color: '#6b7280' }}>({catalog.length.toLocaleString()} fonts available)</small>
        </summary>

        <div style={{ padding: '8px 0', display: 'grid', gap: 6 }} aria-busy={busy}>
          {/* Category Filter Tabs */}
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 2 }}>
            {Object.entries(CATEGORY_NAMES).map(([key, label]) => (
              <button
                type="button"
                key={key}
                disabled={disabled || busy}
                onClick={() => setCategory(key)}
                style={{
                  fontSize: '10px',
                  padding: '2px 7px',
                  borderRadius: 4,
                  border: category === key ? '1px solid #b91f32' : '1px solid #d1d5db',
                  background: category === key ? '#b91f32' : '#ffffff',
                  color: category === key ? '#ffffff' : '#374151',
                  cursor: 'pointer',
                  fontWeight: category === key ? 700 : 500
                }}
              >
                {label}
              </button>
            ))}
          </div>

          <label htmlFor={searchId} style={{ fontSize: '11px', color: '#4b5563' }}>
            Search {catalog.length.toLocaleString()} fonts ({matching.length.toLocaleString()} matching):
          </label>
          <input
            id={searchId}
            type="search"
            value={query}
            disabled={disabled || busy}
            placeholder="Type font name (e.g. Orbitron, Creepster, Medieval, Poppins)…"
            onChange={event => setQuery(event.target.value)}
            aria-controls={listId}
            style={{ padding: '6px 8px', fontSize: '12px', borderRadius: 6, border: '1px solid #d1d5db' }}
          />

          <select
            id={listId}
            size={Math.min(9, Math.max(3, matching.length))}
            value={matching.some(f => norm(f.name) === norm(value)) ? matching.find(f => norm(f.name) === norm(value))?.name : ''}
            disabled={disabled || busy || !matching.length}
            onChange={event => {
              if (event.target.value) void choose(event.target.value);
            }}
            style={{ width: '100%', minWidth: 0, maxHeight: 200, fontSize: '12px', padding: 4 }}
          >
            {!matching.length && <option value="">No matching fonts</option>}
            {matching.map(font => (
              <option key={font.name} value={font.name}>
                {font.name} · {CATEGORY_NAMES[font.cat] || font.cat}
              </option>
            ))}
          </select>

          {busy && <small role="status" style={{ color: '#b91f32' }}>Loading selected font…</small>}
          {error && <small role="alert" style={{ color: '#dc2626' }}>{error}</small>}
        </div>
      </details>
    </div>
  );
}
